import classNames from 'classnames';
import React from 'react';
import { StorageProviderKey } from '../../../models/storageProviderKeys';
import dropbox from '../../../assets/dropbox.svg';
import googleDrive from '../../../assets/googledrive.svg';
import mixcloud from '../../../assets/mixcloud.svg';
import oneDrive from '../../../assets/onedrive.svg';
import soundCloud from '../../../assets/soundcloud.svg';
import s from './brandChip.module.scss';

/**
 * A service's logo where its name used to be.
 *
 * The logos are the ones the header already uses, but the header is light
 * grey and the settings page is nearly black, and half of these marks carry
 * dark text (Google Drive's grey wordmark, Mixcloud's, YouTube's). So each
 * sits on a small light chip, which is also what the brands' own guidelines
 * ask for on a dark ground. The name stays in the alt text.
 */

export type Brand =
  'googleDrive' | 'oneDrive' | 'dropbox' | 'youTube' | 'soundCloud' | 'mixcloud' | 'spotify';

const brands: Record<Brand, { src: string; name: string; height?: string }> = {
  googleDrive: { src: googleDrive, name: 'Google Drive' },
  oneDrive: { src: oneDrive, name: 'OneDrive' },
  dropbox: { src: dropbox, name: 'Dropbox' },
  // The two PNGs live in public/icons because the header loads them by URL.
  youTube: { src: '/icons/yt_logo_rgb_light.png', name: 'YouTube' },
  soundCloud: { src: soundCloud, name: 'SoundCloud', height: '0.75em' },
  mixcloud: { src: mixcloud, name: 'Mixcloud', height: '0.75em' },
  spotify: { src: '/icons/Spotify_Logo_RGB_Green.png', name: 'Spotify' },
};

/**
 * The logo for each storage provider. `Local` is the development-only
 * browser provider and has no brand, so it keeps its readable name.
 */
export const storageProviderBrands: { [P in StorageProviderKey]?: Brand } = {
  [StorageProviderKey.GoogleDrive]: 'googleDrive',
  [StorageProviderKey.OneDrive]: 'oneDrive',
  [StorageProviderKey.Dropbox]: 'dropbox',
};

interface Props {
  brand: Brand;
  className?: string;
}

export default function BrandChip({ brand, className }: Props) {
  const { src, name, height } = brands[brand];

  return (
    <span className={classNames(s.chip, className)} title={name}>
      <img src={src} alt={name} className={s.logo} style={height ? { height } : undefined} />
    </span>
  );
}
